import {
  Button,
  ButtonGroup,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  Link,
  Typography
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Box from "@mui/material/Box";
import { push } from "connected-react-router";
import { startCase } from "lodash";
import moment from "moment";
import * as React from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { match as matchInterface, useLocation } from "react-router";
import { useState } from "react";
import { serviceProviderActions } from "../../actions";
import * as clientActions from "../../actions/client";
import * as serviceActions from "../../actions/services";
import { batchUploadClient } from "../../api/client";
import {
  clientUploadSchema,
  sampleClientUploadData
} from "../../components/CsvUploader/ClientUploadSchema";
import CsvUploader, { MenuItem } from "../../components/CsvUploader/CsvUploader";
import OkhatiDialog from "../../components/Dialog/Dialog";
import List, { Filters } from "../../components/List";
import { ListActions, Menu } from "../../components/OList";
import PageControl from "../../components/PageControl";
import { t, tl } from "../../components/translate";
import { downloadCSV } from "../../helpers/files";
import { ageFormatter } from "../../helpers/formatters";
import { ReverseMap } from "../../helpers/types";
import useDebounce from "../../hooks/useDebounce";
import useMobileScreen from "../../hooks/useMobileScreen";
import useQueryParams from "../../hooks/useQueryParams";
import { Client } from "../../interfaces/ClientInterface";
import { clientFullNameSelector } from "../../reducers/client";
import { getCurrentSubscription } from "../../slices/subscriptionSlice";
import { IThunkDispatch, RootState } from "../../store";
import Can from "../Policy/Can";
import ClientCreateEdit from "./ClientCreateEdit";
import ClientInfo from "./ClientInfo/ClientInfo";
import "./ClientList.scss";
import useAssociateCompanies from "../../hooks/useAssociateCompanies";
import ClientSearchComponents, {
  CLIENT_SEARCH_BY,
  ClientSearchByType
} from "./ClientSearchComponents";
import ReportsPanel from "../Reports/Panel/ReportsPanel";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import SearchOtherBranchClients from "./SearchOtherBranchClients";
import { getRcFamily } from "../../api/resourceCentre";
import { notificationAdd } from "../../actions/notification";
import { ResourceCentre } from "../../interfaces/ResourceCentreInterface";
import useCan from "../../hooks/useCan";
import { ClientSettingsPanel } from "../ResourceCentre/ModuleSettings/ClientSettings/ClientSettingsPanel";

// @ts-expect-error - css
import style from "./style.module.css";
import SearchByFingerprintButton from "./SearchByFingerprintButton";
import FingerprintIdentifyPanel from "./FingerprintIdentifyPanel";
import useGetOnGoing from "../../hooks/useGetOnGoing";
import { Type } from "../../actions/client";

interface QueryTypes {
  q: string;
  filter: string;
  subFilter?: string;
  page: number;
  pageSize: number;
  searchBy: ClientSearchByType;
  clientOriginType?: string;
  clientStatus: CLIENT_STATUS;
  showCorporateClient: string | null;
}

interface ClientListInterface {
  actions: {
    goto: (url: string) => void;
    loadClients: (query?: QueryTypes) => Promise<void>;
    loadFullClient: (id) => void;
    loadServiceProviders: () => void;
    loadServices: () => void;
  };
  clients: Array<Client>;
  mode?: string;
  clientId?: string | number;
  client?: Client;
  match?: matchInterface<{ [x: string]: string | undefined }>;
  totalClients: number;
  isCorporateClient?: boolean;
}

enum ClientOriginType {
  LOCAL = "local",
  LINKED = "linked"
}

enum CLIENT_STATUS {
  REGISTERED = "registered",
  LEAD = "lead"
}

enum ClientFilterType {
  ALL = "all",
  VISITED_TODAY = "visitedToday",
  VISITED_LAST_SEVEN_DAYS = "visitedLastSevenDays",
  VISITED_LAST_180_DAYS = "visitedLast180Days",
  CHECKED_IN_CLIENTS = "checkedInClients"
}

enum ClientVisitedTodayFilterType {
  NEW = "new",
  OLD = "old"
}

const visitedTodayfilterData = {
  filters: [
    { key: ClientVisitedTodayFilterType.NEW, title: "New" },
    { key: ClientVisitedTodayFilterType.OLD, title: "Old" }
  ]
};

const clientOriginTypeFilterData = (canListCorporateClient: boolean) => ({
  filters: [
    { key: ClientOriginType.LOCAL, title: "Clients" },
    { key: ClientOriginType.LINKED, title: "Shared Clients" },
    { key: CLIENT_STATUS.LEAD, title: "Lead" },
    canListCorporateClient ? { key: "corporate", title: "Corporate" } : {}
  ]
});

type ClientFilter = ReverseMap<typeof ClientFilterType>;
export const CLIENT_BATCH_UPLOAD_LIMIT = 1000;
const pageSize = 100;
const pageLimit = 9;

export const getCustomerNumber = (number: string): string =>
  number && number.length < 5 ? number.padStart(5, "0") : number || "";

const buildClientUrl = ({
  filter,
  subFilter,
  clientOriginType,
  clientStatus,
  showCorporateClient
}) => {
  const params = new URLSearchParams();

  if (filter) params.set("filter", filter);
  if (subFilter) params.set("subFilter", subFilter);
  if (clientOriginType) params.set("clientOriginType", clientOriginType);
  if (clientStatus) params.set("clientStatus", clientStatus);
  if (showCorporateClient) params.set("showCorporateClient", "true");

  return `/clients?${params.toString()}`;
};

export const ClientList = ({
  actions,
  clients,
  mode,
  clientId,
  client,
  match,
  totalClients,
  isCorporateClient
}: ClientListInterface): React.JSX.Element => {
  const query = useQueryParams();
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();
  const isMobileScreen = useMobileScreen();
  const location = useLocation<{ search: string }>();

  const currentSubscription = useSelector(
    (state: RootState) => state.subscriptions.currentSubscription
  );
  const isLoading = useGetOnGoing(Type.CLIENTS_GET);

  const filterParam = query.get("filter") as ClientFilterType;
  const subFilterParam = query.get("subFilter") as ClientVisitedTodayFilterType;
  const clientOriginTypeParam = query.get("clientOriginType") as ClientOriginType;
  const showCorporateClient = query.get("showCorporateClient");
  const canCreateCorporateClient = useCan(["clients:createCorporateClient"], {});
  const canListCorporateClient = useCan(["clients:listCorporateClient"], {});

  const [page, setPage] = React.useState<number>(0);
  const [text, setText] = React.useState<string>("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [isPageChanged, setIsPageChanged] = React.useState(false);
  const [showWarningModal, setShowWarningModal] = React.useState(false);
  const [debounceLoading, setDebounceLoading] = React.useState<boolean>(false);
  const [selectedClient, setSelectedClient] = React.useState<Client | null>(null);
  const [rcFamily, setRcFamily] = React.useState<Array<Partial<ResourceCentre>>>([]);
  const [searchBy, setSearchBy] = React.useState<ClientSearchByType>(CLIENT_SEARCH_BY.NAME);
  const [openSearchOtherBranchClients, setOpenSearchOtherBranchClients] =
    React.useState<boolean>(false);

  const [openSearchByFingerprintPanel, setOpenSearchByFingerprintPanel] = useState(false);

  const [filter, setFilter] = React.useState<ClientFilter>(() =>
    Object.values(ClientFilterType).indexOf(filterParam) !== -1 ? filterParam : ClientFilterType.ALL
  );
  const [clientStatus, setClientStatus] = React.useState(CLIENT_STATUS.REGISTERED);
  const [subFilter, setSubFilter] = React.useState<ClientVisitedTodayFilterType>(() =>
    Object.values(ClientVisitedTodayFilterType).indexOf(subFilterParam) !== -1
      ? subFilterParam
      : ClientVisitedTodayFilterType.NEW
  );
  const [clientOriginType, setClientOriginType] = React.useState(() =>
    Object.values(ClientOriginType).indexOf(clientOriginTypeParam) !== -1
      ? clientOriginTypeParam
      : ClientOriginType.LOCAL
  );

  const clientsMemo: Client[] = React.useMemo(
    () =>
      (clients || []).map((draft) => ({
        ...draft,
        fullName: [draft.firstName, draft.lastName].join(" ")
      })),
    [clients]
  );

  const delay = isPageChanged ? 300 : 0;
  const dependencies = React.useMemo(
    () => ({
      delay,
      page,
      text,
      filter,
      searchBy
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, text, filter, searchBy]
  );

  const debouncedCallback = React.useCallback(async () => {
    if (!mode) {
      await actions.loadClients({
        q: text,
        filter,
        page,
        pageSize,
        searchBy,
        subFilter,
        clientOriginType,
        clientStatus,
        showCorporateClient
      });
      setLoading(false);
      setIsPageChanged(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    text,
    filter,
    page,
    mode,
    searchBy,
    subFilter,
    clientOriginType,
    clientStatus,
    showCorporateClient
  ]);

  useDebounce(dependencies, debouncedCallback);

  const handleViewClose = () => {
    setSelectedClient(null);
    actions.goto(`/clients${location.search || ""}`);
  };

  const handleDropdownClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const filterData = {
    filters: [
      { key: ClientFilterType.ALL, title: tl(`client.filter.all`) },
      { key: ClientFilterType.CHECKED_IN_CLIENTS, title: tl(`client.filter.checkedInClients`) },
      { key: ClientFilterType.VISITED_TODAY, title: tl(`client.filter.visitedToday`) },
      {
        key: ClientFilterType.VISITED_LAST_SEVEN_DAYS,
        title: tl(`client.filter.visitedLastSevenDays`)
      },
      {
        key: ClientFilterType.VISITED_LAST_180_DAYS,
        title: tl(`client.filter.visitedLast180Days`)
      }
    ]
  };

  // effects
  React.useEffect(() => {
    const cId = match?.params?.id;
    if (cId && clients.length > 0) {
      let queryClient;
      if (cId.length === 12) {
        queryClient = clients.find((c) => c.uniqueId === cId);
      } else {
        queryClient = clients.find((c) => c.id === Number(cId));
        if (!queryClient) {
          actions.loadFullClient(cId);
        }
      }
      setSelectedClient(queryClient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.params?.id, clients]);

  React.useEffect(() => {
    if (mode === "read" && clientId && client) {
      if (client.active) {
        setSelectedClient(client);
      }
    } else if (clientId && !client)
      actions.loadClients({
        q: "",
        filter,
        page,
        pageSize,
        searchBy,
        subFilter,
        clientOriginType,
        clientStatus,
        showCorporateClient
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, mode]);

  React.useEffect(() => {
    if (!currentSubscription) {
      dispatch(getCurrentSubscription(rc?.id));
    }
  }, [currentSubscription, dispatch, rc?.id]);

  React.useEffect(() => {
    if (!rc?.id) return;

    (async () => {
      try {
        const rcs = await getRcFamily(rc.id);
        setRcFamily(rcs);
      } catch (err) {
        if ((err as unknown as { status?: number }).status !== 404) {
          dispatch(
            notificationAdd({
              id: new Date().getMilliseconds(),
              variant: "error",
              message: "Failed to fetch family IDs for the resource centre.",
              autoTimeout: true
            })
          );
        }
      }
    })();
  }, [rc?.id]);

  useAssociateCompanies();
  const userContext = useSelector((state: RootState) => state.userContext);

  const isServiceProvider = userContext?.userCreds?.authenticable === "serviceProvider";
  const isLeadClientEnabled = rc?.settings?.clientSettings.enableLeadClients;
  const dropdownMenuItems = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <ButtonGroup color="primary" variant="contained" ref={anchorEl} aria-label="split button">
        <Button
          onClick={() => {
            handleDropdownClose();
            actions.goto(`/clients/new${location.search || ""}`);
          }}
          data-testmation="createClient"
        >
          {isMobileScreen ? tl("create") : tl("clients.createClient")}
        </Button>

        <Button
          onClick={handleDropdownClick}
          size="small"
          data-testmation="clientListCreateDropdown"
        >
          <ArrowDropDownIcon />
        </Button>

        <MuiMenu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleDropdownClose}
        >
          {canCreateCorporateClient && (
            <MuiMenuItem
              data-testmation="createCorporateClient"
              onClick={() => {
                handleDropdownClose();
                actions.goto(`/clients/new/corporate${location.search || ""}`);
              }}
            >
              Create Corporate Client
            </MuiMenuItem>
          )}

          {rcFamily?.length > 0 && (
            <MuiMenuItem
              onClick={() => {
                setOpenSearchOtherBranchClients(true);
                handleDropdownClose();
              }}
            >
              Search Branch Clients
            </MuiMenuItem>
          )}
        </MuiMenu>
      </ButtonGroup>
      <SearchByFingerprintButton onClick={() => setOpenSearchByFingerprintPanel(true)} />
      <ClientSettingsPanel />
      <ReportsPanel module="client" />
    </Box>
  );

  return (
    <Can policyAccessKey="clients:listClient">
      <OkhatiDialog
        title="Warning"
        description="Client list is limited to 10 pages only. Please try searching from the search box."
        readMode={false}
        open={showWarningModal}
        next={() => setShowWarningModal(false)}
        cancel={() => setShowWarningModal(false)}
      />
      <div className={`clientList ${style.root}`}>
        <Box height="calc(100% - 44px)">
          {/* client origin type filter */}
          <Box sx={{ px: 2, mt: 2 }}>
            <Filters
              filter={clientOriginType}
              onFilter={(newOriginType) => {
                const newSubFilter =
                  filter === ClientFilterType.VISITED_TODAY
                    ? ClientVisitedTodayFilterType.NEW
                    : null;

                const newFilters = {
                  filter,
                  subFilter: newSubFilter,
                  clientOriginType: newOriginType,
                  clientStatus: null,
                  showCorporateClient: false
                };

                setClientOriginType(newOriginType);

                if (newOriginType === CLIENT_STATUS.LEAD) {
                  setClientStatus(newOriginType);
                  newFilters.clientOriginType = "";
                  newFilters.clientStatus = CLIENT_STATUS.LEAD;
                } else if (newOriginType === "corporate") {
                  newFilters.showCorporateClient = true;
                  newFilters.clientOriginType = "";
                } else {
                  setClientStatus(CLIENT_STATUS.REGISTERED);
                  newFilters.clientStatus = CLIENT_STATUS.REGISTERED;
                }

                const url = buildClientUrl({
                  ...newFilters
                });
                dispatch(push(url));
              }}
              filterData={
                isLeadClientEnabled
                  ? clientOriginTypeFilterData(canListCorporateClient)
                  : {
                      filters: clientOriginTypeFilterData(canListCorporateClient).filters.filter(
                        (item) => item.key !== CLIENT_STATUS.LEAD
                      )
                    }
              }
            />
          </Box>

          {/* main header content */}
          <Box display="flex" mx={2} gap={1}>
            <Box flex={1}>
              {clientStatus === CLIENT_STATUS.REGISTERED && (
                <Filters
                  filter={filter}
                  onFilter={(newFilter) => {
                    setFilter(newFilter);

                    const newSubFilter =
                      newFilter === ClientFilterType.VISITED_TODAY
                        ? ClientVisitedTodayFilterType.NEW
                        : null;

                    const url = buildClientUrl({
                      filter: newFilter,
                      subFilter: newSubFilter,
                      clientOriginType
                    });
                    dispatch(push(url));
                  }}
                  filterData={filterData}
                />
              )}
            </Box>

            <Can policyAccessKey="clients:searchClient">
              <ClientSearchComponents
                value={text}
                loading={loading}
                debounceLoading={debounceLoading}
                onDebounceLoadingChange={(value) => setDebounceLoading(value)}
                onInputClear={() => setText("")}
                onSearchByChange={(value) => {
                  setSearchBy(value);
                  setPage(0);
                }}
                onInputChange={(value) => {
                  if (value?.length > 0) {
                    setLoading(true);
                    setText(value);
                    setPage(0);
                  }
                }}
              />
            </Can>
            {dropdownMenuItems}
          </Box>

          {/* sub filter */}
          {(location?.search || "").includes(ClientFilterType.VISITED_TODAY) && (
            <Box pl={2} flex={1}>
              <Filters
                filter={subFilter}
                onFilter={(newSubFilter) => {
                  setSubFilter(newSubFilter);
                  const url = buildClientUrl({ filter, subFilter: newSubFilter, clientOriginType });
                  dispatch(push(url));
                }}
                filterData={visitedTodayfilterData}
              />
            </Box>
          )}

          <List
            testmationLabel="createClient"
            automation="ClientList"
            createDropdownLabel={isMobileScreen ? "Create" : tl("clients.createClient")}
            hideCreateButton
            rowHeight={50}
            isResponsive
            withoutSearch
            columns={[
              {
                key: "name",
                label: tl("clients.name"),
                sortable: true,
                sortFunction: (a, b) =>
                  `${a.firstName}${a.lastName}`.toLowerCase() >
                  `${b.firstName}${b.lastName}`.toLowerCase()
                    ? 1
                    : -1,
                formatter: (row) => (
                  <Typography
                    component="div"
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "flex-start"
                    }}
                    title={startCase(clientFullNameSelector(row))}
                  >
                    <Box>{clientFullNameSelector(row).toUpperCase()}</Box>
                    <Box fontSize="small" color="gray">
                      {getCustomerNumber(row.customerNumber)}
                      {row.externalIdentifier
                        ? ` (${t("labPrint.customerIdentifier")}: ${row.externalIdentifier})`
                        : ""}
                      {!!row.registrationNo && ` (Reg. No: ${row.registrationNo})`}
                    </Box>
                  </Typography>
                )
              },
              ...(showCorporateClient
                ? []
                : [
                    {
                      key: "dob",
                      label: tl("clients.age"),
                      formatter: ({ dob }) => (
                        <Typography className="textEllipsis" title={ageFormatter(dob)}>
                          {ageFormatter(dob)}
                        </Typography>
                      ),
                      hideInNarrowView: true
                    },
                    {
                      key: "gender",
                      label: tl("clients.gender"),
                      formatter: (c) => (
                        <Typography>{c.gender ? tl(`clients.gender.${c.gender}`) : ""}</Typography>
                      ),
                      hideInNarrowView: true
                    }
                  ]),
              {
                key: "email",
                label: tl("clients.email"),
                formatter: (c) => (
                  <Typography className="textEllipsis" title={c.email}>
                    {c.email}
                  </Typography>
                ),
                hideInNarrowView: true
              },
              {
                key: "internalNotes",
                label: `${t("clients.InternalNotes")}`,
                sortable: true,
                formatter: ({ internalNotes }) => (
                  <Typography className="textEllipsis" title={internalNotes}>
                    {internalNotes}
                  </Typography>
                ),
                hideInNarrowView: true
              },
              {
                key: "phone",
                label: tl("clients.phone"),
                formatter: (c) => (
                  <Typography className="textEllipsis" title={c.phone}>
                    {!isServiceProvider ? c.phone : "NA"}
                  </Typography>
                )
              },
              {
                key: "created_at",
                label: tl("clients.created_at"),
                sortable: true,
                sortFunction: (a, b) =>
                  +new Date(a.created_at) < +new Date(b.created_at) ? 1 : -1,
                formatter: (c) => (
                  <Typography>{moment(c.created_at).format("YYYY-MM-DD")}</Typography>
                ),
                hideInNarrowView: true
              },
              {
                key: "lastVisitedAt",
                label: tl("clients.lastVisitedAt"),
                sortable: true,
                sortFunction: (a, b) =>
                  +new Date(a.lastVisited_at) < +new Date(b.lastVisited_at) ? 1 : -1,
                formatter: (c) => (
                  <Typography>
                    {c.lastVisitedAt ? moment(c.lastVisitedAt).format("YYYY-MM-DD") : "-"}
                  </Typography>
                ),
                hideInNarrowView: true
              },
              {
                key: "customerNumber",
                label: tl("customerNumber"),
                alwaysHidden: true,
                sortable: true,
                sortFunction: (a, b) => {
                  if (a.customerNumber && b.customerNumber) {
                    return a.customerNumber > b.customerNumber ? 1 : -1;
                  }
                  return `${a.customerNumber}`.toLowerCase() > `${b.customerNumber}`.toLowerCase()
                    ? -1
                    : 1;
                }
              }
            ]}
            data={clientsMemo || []}
            isLoading={isLoading}
            emptyViewContent={
              <div>
                No items to show. The clients shown here are created in past 30 days. <br /> If you
                want to view older clients, Please{" "}
                <Link onClick={() => dispatch(push("/reports/patient/clientReport"))}>
                  visit Client Report.
                </Link>
              </div>
            }
            activeRow={selectedClient && selectedClient.id}
            onRowClick={async (c: Client) => {
              const notSelected = !selectedClient || selectedClient.id !== c.id;
              setSelectedClient(notSelected ? c : null);
              actions.goto(
                notSelected
                  ? `/clients/${c.id}${location.search || ""}`
                  : `/clients${location.search || ""}`
              );
            }}
          >
            <ListActions>
              {() => (
                <Menu>
                  <CsvUploader
                    buttonText="Upload clients (.csv)"
                    columns={clientUploadSchema}
                    requiredFieldInfoText="Note: First name, Last Name and Phone number are mandatory information for importing."
                    createDataApi={batchUploadClient}
                    useBatchUpload
                    limit={CLIENT_BATCH_UPLOAD_LIMIT}
                    maxUploadLimitText={`Maximum upload limit for clients is ${CLIENT_BATCH_UPLOAD_LIMIT}.`}
                    runAfterSave={() => actions.loadClients()}
                    renderAdditionalInfo={() => (
                      <Typography
                        sx={{
                          cursor: "pointer",
                          position: "fixed",
                          right: 20
                        }}
                      >
                        <Link
                          onClick={() => {
                            downloadCSV(
                              "Clients Upload Sample",
                              sampleClientUploadData.data,
                              sampleClientUploadData.headers,
                              (row) => row
                            );
                          }}
                        >
                          View Sample
                        </Link>
                      </Typography>
                    )}
                  />
                  <MenuItem
                    onClick={() =>
                      window.open(
                        "https://docs.google.com/spreadsheets/d/1z5QsYWEaX4ifouIOzsatXcahu_t6BKDZ",
                        "_blank"
                      )
                    }
                  >
                    {tl("viewSample")}
                  </MenuItem>
                </Menu>
              )}
            </ListActions>
          </List>
        </Box>
        <Box
          sx={{
            height: "40px",
            width: "calc(100vw - 50px)",
            pl: "50px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 -3px 6px 0 rgba(0, 0, 0, 0.16)",
            backgroundColor: "#f0f0f0",
            overflow: "hidden",
            position: "fixed",
            bottom: 0,
            right: 0
          }}
        >
          <Typography>Total Clients: {totalClients || 0}</Typography>
          <PageControl
            page={page}
            pageSize={pageSize}
            onSetPage={(v) => {
              if (v > pageLimit) {
                setShowWarningModal(true);
                return;
              }
              setPage(v);
              setIsPageChanged(true);
            }}
            maximumDataCount={totalClients}
            allowNextAtEnd
            endAlertMessage="The clients shown here are created in past 30 days. If you want to view older clients, Please visit Client Report."
            pushTo="reports/patient/clientReport"
          />
        </Box>

        {selectedClient && <ClientInfo id={selectedClient.id} handleViewClose={handleViewClose} />}

        {mode && ["create", "edit"].includes(mode) && (
          <ClientCreateEdit
            clientId={clientId}
            mode={mode}
            onCancel={() => actions.goto(`/clients${location.search || ""}`)}
            showLastCreated={() => actions.goto("/clients")}
            isCorporateClient={isCorporateClient || Boolean(selectedClient?.isCorporateClient)}
          />
        )}

        {openSearchOtherBranchClients && (
          <SearchOtherBranchClients
            onClose={() => setOpenSearchOtherBranchClients(false)}
            rcFamily={rcFamily}
          />
        )}
        {openSearchByFingerprintPanel && (
          <FingerprintIdentifyPanel onClose={() => setOpenSearchByFingerprintPanel(false)} />
        )}
      </div>
    </Can>
  );
};

export default connect(
  (state: RootState, { clientId }: { clientId?: number }) => ({
    clients: state.clients.collection,
    client: state.clients.collection?.find(({ id }) => id === Number(clientId)),
    totalClients: state.clients.total
  }),
  (dispatch: IThunkDispatch) => ({
    actions: {
      loadServiceProviders: () =>
        dispatch(serviceProviderActions.getResourceCentreServiceProviders({})),
      loadServices: () => dispatch(serviceActions.getServices()),
      goto: (url) => dispatch(push(url)),
      loadClients: (query) => dispatch(clientActions.getClients(query)),
      loadFullClient: (id) => dispatch(clientActions.getClientById(id))
    }
  })
)(ClientList);
